import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useWS } from "@/context/WSContext";
import { Zap, Wallet as WalletIcon, Loader2, Plus, ArrowUpRight, ArrowDownRight, Coins } from "lucide-react";
import { toast } from "sonner";
import LevelBadge from "@/components/LevelBadge";

const PACK_META = {
  pack_2:   { label: "$2",   tag: "warmup" },
  pack_5:   { label: "$5",   tag: "spark" },
  pack_10:  { label: "$10",  tag: "surge",  popular: false },
  pack_25:  { label: "$25",  tag: "boost",  popular: true },
  pack_50:  { label: "$50",  tag: "overdrive" },
  pack_100: { label: "$100", tag: "whale" },
  pack_500: { label: "$500", tag: "mythic" },
};

function LevelProgress({ label, describe }) {
  const level = describe?.level || 0;
  const tier = describe?.tier;
  const nextC = describe?.next_level_cents;
  const startC = describe?.current_level_start_cents || 0;
  const curC = describe?.lifetime_cents || 0;
  const usd = (n) => `$${((n || 0) / 100).toFixed(0)}`;
  const pct = Math.round((describe?.progress || 0) * 100);
  const accent = tier?.accent || "#39FF14";
  const kind = label === "GIFTER" ? "gifter" : "buyer";

  return (
    <div
      data-testid={`level-progress-${kind}`}
      className="p-5 border border-white/10 bg-[#0A0A0C] flex gap-5 items-center"
    >
      <LevelBadge level={level} kind={kind} size="lg" />
      <div className="flex-1 min-w-0">
        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40 mb-1">
          {label} · lifetime
        </p>
        <p className="font-display font-black text-3xl text-white leading-none">
          ${(curC / 100).toFixed(2)}
        </p>
        {nextC != null ? (
          <>
            <div className="mt-4 h-1.5 w-full bg-white/5 relative overflow-hidden">
              <div
                className="absolute inset-y-0 left-0 transition-[width]"
                style={{
                  width: `${pct}%`,
                  background: `linear-gradient(90deg, ${accent}, ${tier?.accent2 || "#fff"})`,
                  boxShadow: `0 0 10px ${accent}80`,
                }}
              />
            </div>
            <p className="mt-2 font-mono text-[10px] uppercase tracking-widest text-white/40">
              {pct}% → L{level + 1} at{" "}
              <span style={{ color: accent }}>{usd(nextC)}</span>
            </p>
          </>
        ) : (
          <p className="mt-4 font-mono text-[10px] uppercase tracking-widest text-[#FF007F]">
            /// max level reached · reality.rewritten
          </p>
        )}
      </div>
    </div>
  );
}

export default function Wallet() {
  const { user, checkAuth } = useAuth();
  const { subscribe } = useWS();
  const nav = useNavigate();
  const location = useLocation();
  const [wallet, setWallet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedPack, setSelectedPack] = useState("pack_25");
  const [customAmount, setCustomAmount] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [pollingSession, setPollingSession] = useState(null);

  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }
    api.get("/wallet").then((r) => setWallet(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [user]);

  // Live wallet_updated pushes
  useEffect(() => {
    if (!user) return;
    const unsub = subscribe((payload) => {
      if (payload?.type !== "wallet_updated") return;
      api.get("/wallet").then((r) => setWallet(r.data)).catch(() => {});
      checkAuth?.();
    });
    return unsub;
  }, [subscribe, user, checkAuth]);

  // Handle Stripe return
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const sid = params.get("topup_session_id");
    const cancelled = params.get("topup_cancelled");
    if (cancelled) {
      toast.error("Top-up cancelled");
      nav("/wallet", { replace: true });
      return;
    }
    if (!sid) return;
    setPollingSession(sid);
    let tries = 0;
    const iv = setInterval(async () => {
      tries += 1;
      try {
        const r = await api.get(`/wallet/topup/status/${sid}`);
        if (r.data?.credited) {
          clearInterval(iv);
          setPollingSession(null);
          toast.success(`+${(r.data.amount_cents / 100).toFixed(2)} USD added to wallet`);
          const w = await api.get("/wallet");
          setWallet(w.data);
          checkAuth?.();
          nav("/wallet", { replace: true });
        }
      } catch { /* poll again */ }
      if (tries >= 20) {
        clearInterval(iv);
        setPollingSession(null);
      }
    }, 1500);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const useCustom = customAmount.trim() !== "";

  const buy = async () => {
    if (submitting) return;
    const payload = { origin_url: window.location.origin };
    if (useCustom) {
      const n = parseFloat(customAmount);
      if (!n || n < 2 || n > 2000) {
        toast.error("Custom amount must be between $2 and $2000");
        return;
      }
      payload.custom_amount = n;
    } else {
      payload.package_id = selectedPack;
    }
    setSubmitting(true);
    try {
      const res = await api.post("/wallet/topup/checkout", payload);
      if (res.data?.url) {
        window.location.href = res.data.url;
      } else {
        throw new Error("Missing checkout URL");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout");
      setSubmitting(false);
    }
  };

  const totalUsd = useMemo(() => ((wallet?.spendable_cents || wallet?.wallet_balance_cents || 0) / 100).toFixed(2), [wallet]);
  const purchasedUsd = useMemo(() => ((wallet?.wallet_balance_cents || 0) / 100).toFixed(2), [wallet]);
  const earningsUsd = useMemo(() => ((wallet?.earnings_cents || 0) / 100).toFixed(2), [wallet]);
  const hasEarnings = (wallet?.earnings_cents || 0) > 0;

  if (!user) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center text-center px-6">
        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FF007F] mb-3">// wallet.locked</p>
        <p className="text-white/60 mb-6">Sign in to buy & spend coins.</p>
        <button
          data-testid="wallet-signin-btn"
          onClick={() => nav("/login")}
          className="bg-[#39FF14] text-black font-bold uppercase tracking-widest text-xs px-6 py-3"
        >
          Log in
        </button>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-6 w-6 text-[#FF007F] animate-spin" />
      </div>
    );
  }

  return (
    <div className="md:ml-60 min-h-screen pb-24 md:pb-12">
      <div className="max-w-4xl mx-auto px-6 pt-10 space-y-8">
        {/* Hero balance */}
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FF007F] mb-2">
            // your.wallet
          </p>
          <h1
            className="font-display font-black text-4xl md:text-5xl mb-3 flex items-center gap-3 glitch"
            data-text="COIN VAULT"
          >
            <WalletIcon className="h-7 w-7 text-[#39FF14]" strokeWidth={2} />
            COIN VAULT
          </h1>
          <p className="text-white/40 text-sm font-mono">
            top up · gift creators from balance · level up.
          </p>
        </div>

        <div
          data-testid="wallet-balance-hero"
          className="relative overflow-hidden border border-[#39FF14]/40 bg-gradient-to-br from-[#39FF14]/10 via-black to-black p-8"
        >
          <div
            className="absolute -top-10 -right-10 w-40 h-40 rounded-full pointer-events-none"
            style={{ background: "radial-gradient(circle, rgba(57,255,20,0.25), transparent 70%)" }}
          />
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#39FF14]/80">
            spendable
          </p>
          <div className="flex items-baseline gap-3 mt-1">
            <span
              data-testid="wallet-balance-usd"
              className="font-display font-black text-6xl md:text-7xl text-white leading-none"
              style={{ textShadow: "0 0 30px rgba(57,255,20,0.4)" }}
            >
              ${totalUsd}
            </span>
            <span className="font-mono text-xs uppercase tracking-widest text-white/40">
              {(wallet?.spendable_cents || 0).toLocaleString()} coins
            </span>
          </div>

          {/* Breakdown pills */}
          <div className="mt-5 grid grid-cols-2 gap-3">
            <div
              data-testid="wallet-purchased-chip"
              className="px-3 py-2 border border-white/10 bg-black/40"
            >
              <p className="font-mono text-[9px] uppercase tracking-widest text-white/40">
                wallet · purchased
              </p>
              <p className="font-display font-black text-lg text-white leading-tight">
                ${purchasedUsd}
              </p>
            </div>
            <div
              data-testid="wallet-earnings-chip"
              className={`px-3 py-2 border ${hasEarnings ? "border-[#FF007F]/40 bg-[#FF007F]/5" : "border-white/10 bg-black/40"}`}
            >
              <p className="font-mono text-[9px] uppercase tracking-widest text-white/40 flex items-center gap-1">
                earnings · regiftable
                {hasEarnings && <span className="text-[#FF007F]">·</span>}
                {hasEarnings && (
                  <span className="text-[#FF007F] font-bold">tip.or.cash</span>
                )}
              </p>
              <p className={`font-display font-black text-lg leading-tight ${hasEarnings ? "text-[#FF007F]" : "text-white/50"}`}>
                ${earningsUsd}
              </p>
            </div>
          </div>

          {hasEarnings && (
            <p className="mt-3 font-mono text-[10px] uppercase tracking-widest text-white/50">
              // tips.draw.earnings.first · cashout via /earnings
            </p>
          )}
          {pollingSession && (
            <p className="mt-3 font-mono text-[10px] uppercase tracking-widest text-[#FFB800] flex items-center gap-2">
              <Loader2 className="h-3 w-3 animate-spin" />
              confirming top-up · hold tight
            </p>
          )}
        </div>

        {/* Levels grid */}
        <div className="grid gap-4 md:grid-cols-2">
          <LevelProgress label="GIFTER" describe={wallet?.gifter} />
          <LevelProgress label="BUYER" describe={wallet?.buyer} />
        </div>

        {/* Coin top-up — open to all users */}
        {wallet?.can_topup ? (
          <section data-testid="wallet-topup">
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40 mb-2">
              // top.up
            </p>
            <h2 className="font-display font-black text-2xl mb-4 flex items-center gap-2">
              <Plus className="h-5 w-5 text-[#39FF14]" strokeWidth={2.5} />
              add coins
            </h2>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {(wallet?.packs || []).map((p) => {
                const meta = PACK_META[p.id] || {};
                const active = !useCustom && selectedPack === p.id;
                return (
                  <button
                    key={p.id}
                    data-testid={`pack-${p.id}`}
                    onClick={() => {
                      setSelectedPack(p.id);
                      setCustomAmount("");
                    }}
                    className={`relative py-4 flex flex-col items-center border transition-all ${
                      active
                        ? "border-[#39FF14] bg-[#39FF14]/10 shadow-[0_0_18px_rgba(57,255,20,0.35)]"
                        : "border-white/15 hover:border-white/40"
                    }`}
                  >
                    {meta.popular && (
                      <span className="absolute -top-2 left-1/2 -translate-x-1/2 bg-[#FF007F] text-white text-[9px] uppercase font-bold tracking-widest px-2 py-0.5">
                        popular
                      </span>
                    )}
                    <span className="font-display font-black text-xl text-white">
                      {meta.label || `$${p.usd}`}
                    </span>
                    <span className="font-mono text-[9px] uppercase tracking-widest text-white/50 mt-0.5">
                      {p.coins.toLocaleString()} coins
                    </span>
                    <span className="font-mono text-[9px] uppercase tracking-widest text-[#39FF14]/60 mt-0.5">
                      {meta.tag}
                    </span>
                  </button>
                );
              })}
            </div>

            <div className="mt-4">
              <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/50 mb-2 block">
                // or.custom.amount
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 font-display font-bold text-white/40">
                  $
                </span>
                <input
                  data-testid="wallet-custom-amount"
                  type="number"
                  min="2"
                  max="2000"
                  step="1"
                  inputMode="decimal"
                  placeholder="0.00"
                  value={customAmount}
                  onChange={(e) => setCustomAmount(e.target.value)}
                  className="w-full bg-black border border-white/15 focus:border-[#39FF14]/60 focus:outline-none py-3 pl-8 pr-3 text-white font-display text-lg tracking-tight"
                />
              </div>
            </div>

            <button
              data-testid="wallet-buy-btn"
              onClick={buy}
              disabled={submitting}
              className="mt-5 w-full bg-[#39FF14] text-black font-bold uppercase tracking-widest py-4 hover:shadow-[0_0_30px_rgba(57,255,20,0.6)] transition-all flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" /> connecting to stripe…
                </>
              ) : (
                <>
                  <Coins className="h-4 w-4" />
                  buy coins
                </>
              )}
            </button>
            <p className="font-mono text-[10px] uppercase tracking-widest text-white/30 text-center mt-2">
              secured by stripe
            </p>
          </section>
        ) : (
          <section data-testid="wallet-topup-restricted" className="border border-white/10 bg-[#0A0A0C] p-6">
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FF007F] mb-2">
              // access.restricted
            </p>
            <h2 className="font-display font-black text-xl text-white mb-2">
              coin purchases are admin-only
            </h2>
            <p className="text-white/60 text-sm leading-relaxed">
              Direct coin top-ups are currently disabled for this account. You can still spend earned coins on boosts, cash out earnings, and receive tips normally.
            </p>
          </section>
        )}

        {/* History */}
        <section data-testid="wallet-history">
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40 mb-2">
            // recent.activity
          </p>
          <h2 className="font-display font-black text-2xl mb-4">history</h2>
          {(wallet?.recent || []).length === 0 ? (
            <div className="py-10 text-center border border-white/10 bg-[#0A0A0C]">
              <p className="text-white/40 text-sm">No activity yet.</p>
            </div>
          ) : (
            <ul className="divide-y divide-white/5 border border-white/10 bg-[#0A0A0C]">
              {wallet.recent.map((e) => {
                const positive = e.delta_cents > 0;
                const isTopup = e.kind === "topup";
                return (
                  <li
                    key={e.id}
                    data-testid={`wallet-entry-${e.id}`}
                    className="flex items-center gap-3 px-4 py-3"
                  >
                    <div
                      className={`h-9 w-9 flex items-center justify-center border ${
                        positive
                          ? "border-[#39FF14]/40 text-[#39FF14] bg-[#39FF14]/5"
                          : "border-[#FF007F]/40 text-[#FF007F] bg-[#FF007F]/5"
                      }`}
                    >
                      {positive ? (
                        <ArrowDownRight className="h-4 w-4" strokeWidth={2.4} />
                      ) : (
                        <ArrowUpRight className="h-4 w-4" strokeWidth={2.4} />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-sm font-semibold">
                        {isTopup
                          ? "Coins purchased"
                          : e.kind === "gift_sent"
                          ? "Gift sent"
                          : e.kind === "gift_sent_stripe"
                          ? "Gift sent (stripe)"
                          : e.kind}
                      </p>
                      <p className="font-mono text-[10px] uppercase tracking-widest text-white/40">
                        {new Date(e.created_at).toLocaleString()}
                      </p>
                    </div>
                    <p
                      className={`font-display font-black text-lg ${
                        positive ? "text-[#39FF14]" : "text-[#FF007F]"
                      }`}
                    >
                      {positive ? "+" : ""}${(e.delta_cents / 100).toFixed(2)}
                    </p>
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
}
